import React, { useMemo } from 'react';

import { ToggleSwitch } from '@/components/legacy-ui';
import { useMy } from '@/hooks';
import { useUpdateSetting } from '@/hooks/useSetting';
import { SettingActionCard } from '@/sections/settings/components';
import { useAuth } from '@/state/auth';
import { Setting } from '@/types';

const COMPLIANCE_FRAMEWORKS = [
  {
    id: 'nist_csf',
    name: 'NIST CSF 2.0',
    description:
      'NIST Cybersecurity Framework — risk-based subcategory mappings derived from SP 800-53 controls',
  },
  {
    id: 'pci_dss',
    name: 'PCI DSS 4.0',
    description:
      'Payment Card Industry Data Security Standard — requirement-level mappings for cardholder data protection',
  },
  {
    id: 'hipaa',
    name: 'HIPAA Security Rule',
    description:
      'Health Insurance Portability and Accountability Act — technical safeguard mappings for protected health information',
  },
  {
    id: 'soc2',
    name: 'SOC 2 Type II',
    description:
      'Service Organization Control — Trust Services Criteria mappings for service providers',
  },
  {
    id: 'cis',
    name: 'CIS Controls v8',
    description:
      'Center for Internet Security Critical Security Controls — implementation-focused control mappings',
  },
] as const;

interface ComplianceFrameworksSectionProps {
  email?: string;
  editable?: boolean;
}

export const ComplianceFrameworksSection: React.FC<
  ComplianceFrameworksSectionProps
> = ({ email, editable = true }) => {
  const { me, friend } = useAuth();
  const currentEmail = email || friend || me;

  const { data: settings } = useMy(
    { resource: 'setting' },
    { email: currentEmail, staleTime: 30000, gcTime: 300000 }
  );

  const { mutate: updateSetting } = useUpdateSetting(currentEmail);

  const enabledFrameworks: string[] = useMemo(() => {
    const setting = (settings || []).find(
      (s: Setting) => s.name === 'compliance_frameworks'
    );
    if (!setting?.value) return [];
    if (Array.isArray(setting.value)) return setting.value as string[];
    return [];
  }, [settings]);

  const handleToggle = (frameworkId: string, checked: boolean) => {
    const updated = checked
      ? [...enabledFrameworks, frameworkId]
      : enabledFrameworks.filter(f => f !== frameworkId);

    updateSetting({
      name: 'compliance_frameworks',
      value: updated,
    });
  };

  return (
    <>
      {COMPLIANCE_FRAMEWORKS.map(fw => (
        <SettingActionCard
          key={fw.id}
          data-testid={`compliance-${fw.id}-card`}
          action={
            <ToggleSwitch
              id={`compliance-${fw.id}`}
              ariaLabel={`Enable ${fw.name} compliance framework`}
              checked={enabledFrameworks.includes(fw.id)}
              onChange={checked => handleToggle(fw.id, checked)}
              disabled={!editable}
            />
          }
        >
          <div className="flex items-center gap-2">
            <h4 className="font-bold">{fw.name}</h4>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-chariot-text-secondary text-xs">
              {fw.description}
            </span>
          </div>
        </SettingActionCard>
      ))}
    </>
  );
};
